import { scheduleMatchesLanguage } from '@/utils/excursionLanguageFilter';
import {
  computeOptionTotal as sharedComputeOptionTotal,
  getPriceBreakdown as sharedGetPriceBreakdown,
  getGroupPricingMeta as sharedGetGroupPricingMeta,
} from '@/shared/excursionPricing.js';

/**
 * Available dates for an excursion based on site-partner schedules.
 * Schedules are already filtered by backend (for_site=1) so only site partner schedules are in the list.
 *
 * days_of_week: 7-char string, index 0 = Monday, 6 = Sunday. "1" = runs that day.
 * @param {Array} schedules - excursion.schedules (each: start_date, end_date, days_of_week)
 * @param {Object} options - { fromDate?: Date, monthsAhead?: number }
 * @returns {Set<string>} Set of "YYYY-MM-DD" date strings that are available
 */

export function publicRemainingFromCapacityRow(row, fallbackCapacity, soldSeats) {
  const status = String(row?.effective_status || row?.status || '').toLowerCase();
  if (status === 'closed' || status === 'cancelled') return 0;
  if (row && Boolean(row.online_cutoff_reached)) return 0;
  if (row?.remaining_public_seats != null && row.remaining_public_seats !== '') {
    const publicRaw = Number(row.remaining_public_seats);
    if (Number.isFinite(publicRaw) && publicRaw >= 0) return publicRaw;
  }
  if (row?.remaining_seats != null && row.remaining_seats !== '') {
    const remainingRaw = Number(row.remaining_seats);
    if (Number.isFinite(remainingRaw) && remainingRaw >= 0) return remainingRaw;
  }
  const cap = Number(fallbackCapacity);
  const sold = Number(soldSeats);
  if (Number.isFinite(cap) && cap >= 0) {
    return Math.max(cap - (Number.isFinite(sold) && sold >= 0 ? sold : 0), 0);
  }
  return null;
}

/** Match manager Seats / partner portal: show urgency at or below this remaining count. */
export const SEATS_LEFT_URGENCY_THRESHOLD = 6;

/** Parse YYYY-MM-DD as a local calendar date (avoids UTC timezone shift). */
export function parseYmdLocal(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Normalize a sales-capacity API row for website option cards / checkout.
 * @returns {null|{ status: string, seatsToSell: number|null, soldSeats: number, remainingSeats: number|null, soldOut: boolean, blocked: boolean }}
 */
export function normalizeSalesCapacityRow(row, targetDateYmd) {
  if (!row) return null;
  const rowDate = String(row.service_date || '').slice(0, 10);
  if (!rowDate || (targetDateYmd && rowDate !== targetDateYmd)) return null;

  const status = String(row.effective_status || row.status || '').toLowerCase();
  const seatsRaw = Number(row.effective_seats_to_sell ?? row.seats_to_sell);
  const soldRaw = Number(row.sold_seats ?? row.seats_sold ?? row.booked_seats ?? 0);
  const remainingRaw = Number(
    row.remaining_public_seats != null && row.remaining_public_seats !== ''
      ? row.remaining_public_seats
      : row.remaining_seats,
  );
  const seatsToSell = Number.isFinite(seatsRaw) && seatsRaw >= 0 ? seatsRaw : null;
  const soldSeats = Number.isFinite(soldRaw) && soldRaw >= 0 ? soldRaw : 0;
  const blockedByStatus = status === 'closed' || status === 'cancelled';
  const blockedByCutoff = Boolean(row.online_cutoff_reached);
  const soldOut = seatsToSell != null && soldSeats >= seatsToSell;
  let remainingSeats = null;
  if (Number.isFinite(remainingRaw) && remainingRaw >= 0) {
    remainingSeats = remainingRaw;
  } else if (seatsToSell != null) {
    remainingSeats = Math.max(seatsToSell - soldSeats, 0);
  }
  if (blockedByStatus || blockedByCutoff) remainingSeats = 0;

  return {
    status,
    seatsToSell,
    soldSeats,
    remainingSeats,
    soldOut: soldOut || (remainingSeats === 0 && seatsToSell != null),
    blocked: blockedByStatus || blockedByCutoff || soldOut || (remainingSeats === 0 && seatsToSell != null),
    onlineCutoffReached: blockedByCutoff,
    effectiveBufferSeats: Number(row.effective_buffer_seats ?? 0) || 0,
  };
}

/**
 * Public urgency copy for remaining seats (hidden when unlimited or above threshold).
 * @returns {null|{ text: string, tone: 'warning'|'danger' }}
 */
export function getSeatsLeftUrgency(remainingSeats, threshold = SEATS_LEFT_URGENCY_THRESHOLD) {
  if (remainingSeats == null || remainingSeats <= 0) return null;
  if (remainingSeats > threshold) return null;
  if (remainingSeats === 1) {
    return { text: 'Only 1 seat left', tone: 'danger' };
  }
  return { text: `Only ${remainingSeats} seats left`, tone: remainingSeats <= 3 ? 'danger' : 'warning' };
}

export function scheduleHasPickupAvailable(schedule) {
  if (!schedule) return false;
  return (
    schedule.pickup_available === true
    || schedule.pickup_available === 1
    || schedule.pickup_available === '1'
    || Number(schedule.pickup_available) === 1
  );
}

export function getAvailableDatesSet(schedules, options = {}) {
  const set = new Set();
  if (!schedules || !Array.isArray(schedules) || schedules.length === 0) return set;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const fromDate = options.fromDate ? new Date(options.fromDate) : today;
  fromDate.setHours(0, 0, 0, 0);
  const monthsAhead = options.monthsAhead ?? 12;

  const toDate = new Date(fromDate);
  toDate.setMonth(toDate.getMonth() + monthsAhead);

  for (const s of schedules) {
    const start = parseYmdLocal(s.start_date);
    const end = parseYmdLocal(s.end_date);
    const dow = s.days_of_week != null ? String(s.days_of_week) : '1111111';
    if (!start || !end || dow.length < 7) continue;

    const rangeStart = start > fromDate ? start : fromDate;
    const rangeEnd = end < toDate ? end : toDate;
    if (rangeStart > rangeEnd) continue;

    const d = new Date(rangeStart);
    while (d <= rangeEnd) {
      const dayIndex = (d.getDay() + 6) % 7;
      if (dow[dayIndex] === '1') {
        set.add(formatYMD(d));
      }
      d.setDate(d.getDate() + 1);
    }
  }

  return set;
}

/**
 * Schedules that run on a specific date (date within start_date..end_date and day of week in days_of_week).
 * @param {Array} schedules - excursion.schedules (each: id, start_date, end_date, days_of_week, language_name, language_code, ...)
 * @param {Date|string} date - single date
 * @returns {Array} schedules that run on that date
 */
export function getSchedulesForDate(schedules, date) {
  if (!schedules || !Array.isArray(schedules) || schedules.length === 0) return [];
  const d = date instanceof Date ? new Date(date) : parseYmdLocal(date);
  if (!d || Number.isNaN(d.getTime())) return [];
  d.setHours(0, 0, 0, 0);
  const dayIndex = (d.getDay() + 6) % 7; // Mon=0 .. Sun=6

  return schedules.filter((s) => {
    const start = parseYmdLocal(s.start_date);
    const end = parseYmdLocal(s.end_date);
    const dow = s.days_of_week != null ? String(s.days_of_week) : '1111111';
    if (!start || !end || dow.length < 7) return false;
    const dateTime = d.getTime();
    if (dateTime < start.getTime() || dateTime > end.getTime()) return false;
    return dow[dayIndex] === '1';
  });
}

/**
 * Total price for selected passengers and a price row (adult_price, child_price, infant_price).
 * @param {{ adults: number, children: number, infants: number }} passengers
 * @param {{ price?: number, child_price?: number, infant_price?: number }} priceRow - adult price as .price
 * @returns {number}
 */
export function computeOptionTotal(passengers, priceRow) {
  return sharedComputeOptionTotal(passengers, priceRow);
}

/**
 * Price breakdown by pax type for display (adults, children, infants with qty × unit = subtotal).
 * @param {{ adults: number, children: number, infants: number }} passengers
 * @param {{ price?: number, child_price?: number, infant_price?: number }} priceRow
 * @returns {{ label: string, qty: number, unitPrice: number, subtotal: number }[]}
 */
export function getPriceBreakdown(passengers, priceRow) {
  return sharedGetPriceBreakdown(passengers, priceRow);
}

export function getGroupPricingMeta(passengers, scheduleOrRow) {
  return sharedGetGroupPricingMeta(passengers, scheduleOrRow);
}

/** Format "HH:MM" or "HH:MM:SS" to "9:30 AM" / "2:45 PM" */
export function formatTimeAMPM(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.trim().split(':').map(Number);
  if (parts.length < 2) return null;
  let [h, m] = parts;
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function formatYMD(date) {
  const d = date instanceof Date ? date : parseYmdLocal(date);
  if (!d || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Returns an array of Date objects to disable in Flatpickr: all dates from today
 * through the next 12 months that are NOT in availableDatesSet.
 * Use this when passing disable as an array (avoids function-callback init issues).
 */
export function getDisabledDatesArray(availableDatesSet, monthsAhead = 12) {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setMonth(end.getMonth() + monthsAhead);
  const d = new Date(today);
  while (d <= end) {
    if (!availableDatesSet.has(formatYMD(d))) {
      out.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/**
 * For Flatpickr disable option: disable dates that are NOT available (or before today).
 * Defensive: never throws so Flatpickr calendar can always open.
 * @param {Set<string>} availableDatesSet - Set of "YYYY-MM-DD"
 * @returns {(date: Date) => boolean} true = disable this date
 */
export function makeDisableFn(availableDatesSet) {
  return (date) => {
    try {
      if (date == null) return true;
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      if (d < today) return true;
      const ymd = formatYMD(d);
      return !availableDatesSet.has(ymd);
    } catch {
      return false;
    }
  };
}

/**
 * Build per-day bookability status from sales-capacity rows for calendar / suggestions.
 * @returns {Map<string, 'available'|'low'|'unavailable'>}
 */
export function buildDayCapacityStatusMap(capacityRows, schedules, options = {}) {
  const statusByDate = new Map();
  if (!Array.isArray(capacityRows) || !Array.isArray(schedules) || schedules.length === 0) {
    return statusByDate;
  }

  const lang = options.languageCode ? String(options.languageCode).toLowerCase() : '';
  const rowsByDate = new Map();
  capacityRows.forEach((row) => {
    const ymd = String(row?.service_date || '').slice(0, 10);
    if (!ymd) return;
    if (!rowsByDate.has(ymd)) rowsByDate.set(ymd, []);
    rowsByDate.get(ymd).push(row);
  });

  rowsByDate.forEach((rows, ymd) => {
    let schedulesOnDate = getSchedulesForDate(schedules, ymd);
    if (lang) {
      schedulesOnDate = schedulesOnDate.filter((s) => scheduleMatchesLanguage(s, lang));
    }
    if (!schedulesOnDate.length) return;

    let openCount = 0;
    let lowSeats = false;
    let rowsMatched = 0;

    schedulesOnDate.forEach((schedule) => {
      const row = rows.find((r) => Number(r?.schedule_id) === Number(schedule.id));
      if (!row) {
        // No override yet → treat as open for calendar purposes.
        openCount += 1;
        return;
      }
      rowsMatched += 1;
      const normalized = normalizeSalesCapacityRow(row, ymd);
      if (!normalized || normalized.blocked) return;
      openCount += 1;
      if (
        normalized.remainingSeats != null
        && normalized.remainingSeats > 0
        && normalized.remainingSeats <= SEATS_LEFT_URGENCY_THRESHOLD
      ) {
        lowSeats = true;
      }
    });

    if (openCount === 0 && rowsMatched === schedulesOnDate.length) {
      statusByDate.set(ymd, 'unavailable');
    } else if (lowSeats) {
      statusByDate.set(ymd, 'low');
    } else {
      statusByDate.set(ymd, 'available');
    }
  });

  return statusByDate;
}

/**
 * Flatpickr disable list: non-schedule days + known capacity-unavailable days.
 */
export function getCalendarDisabledDatesArray(
  availableDatesSet,
  capacityStatusByYmd,
  monthsAhead = 12,
) {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setMonth(end.getMonth() + monthsAhead);
  const d = new Date(today);
  const statusMap = capacityStatusByYmd instanceof Map
    ? capacityStatusByYmd
    : new Map(Object.entries(capacityStatusByYmd || {}));

  while (d <= end) {
    const ymd = formatYMD(d);
    const notScheduled = !availableDatesSet.has(ymd);
    const capacityUnavailable = statusMap.get(ymd) === 'unavailable';
    if (notScheduled || capacityUnavailable) {
      out.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/**
 * First day of month / last day of month as YMD for capacity prefetch.
 */
export function getMonthDateRangeYmd(year, monthIndex) {
  const from = new Date(year, monthIndex, 1);
  from.setHours(0, 0, 0, 0);
  const to = new Date(year, monthIndex + 1, 0);
  to.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const fromClamped = from < today ? today : from;
  return {
    dateFrom: formatYMD(fromClamped),
    dateTo: formatYMD(to),
  };
}
